/*
 * <twc-content-slideshow> — the "Meet the whisky makers" band.
 *
 * WHY THIS IS NOT HORIZON'S SLIDESHOW. The band's eyebrow and heading are
 * SECTION-level (Confluence 5371723778: the eyebrow text and heading are
 * entered once and show for all carousel slides), so they must sit OUTSIDE
 * the sliding content, in the same flow
 * column as the quote that slides under them. Horizon's slideshow scrolls a
 * row of slides sideways and clips each one (`slideshow-slide` carries
 * `overflow: hidden` and `content-visibility`), so a slide cannot own the
 * photo in the other half of the band, and the section head cannot live
 * inside the scroller. sections/twc-intro-carousel.liquid DOES use the Horizon
 * primitive, because its slides are whole self-contained cards.
 *
 * So this band cross-fades instead: every slide is stacked in one grid cell,
 * its photo is positioned into the media half, and only the current slide is
 * visible. CSS shows the FIRST slide until this file upgrades the element, so
 * a visitor whose JavaScript never lands still reads a complete band.
 *
 * SWIPE (SHOPIFY-1249). On a touch screen a horizontal swipe of more than
 * 40px moves one slide: left for the next, right for the previous, wrapping
 * like the dots do. The band carries `touch-action: pan-y pinch-zoom`, so the
 * browser keeps vertical scrolling and zoom and hands the sideways move to us;
 * a scroll it takes over arrives as `pointercancel` and turns nothing. Mouse
 * pointers are left alone, so a desktop visitor can still select the quote.
 *
 * Every listener rides ONE AbortController, per the theme's custom-element
 * rule: connectedCallback opens it, disconnectedCallback aborts the lot and
 * stops the timer.
 */
(function (global) {
  'use strict';

  if (global.customElements && global.customElements.get('twc-content-slideshow')) return;

  var SLIDE = '[data-twc-cslide-slide]';
  var DOT = '[data-twc-cslide-dot]';

  // A finger that travels further than this sideways turns the slide
  // (SHOPIFY-1249). Shorter moves are taps or wobble and do nothing.
  var SWIPE_PX = 40;

  function list(root, selector) {
    return Array.prototype.slice.call(root.querySelectorAll(selector));
  }

  function reducedMotion() {
    return Boolean(
      global.matchMedia && global.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  var TwcContentSlideshow = class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      var signal = this.controller.signal;
      var self = this;

      this.slides = list(this, SLIDE);
      this.dots = list(this, DOT);
      this.current = 0;
      this.timer = null;
      this.held = false;

      if (this.slides.length === 0) return;

      this.dots.forEach(function (dot, index) {
        dot.addEventListener(
          'click',
          function () {
            self.goTo(index);
            self.restart();
          },
          { signal: signal }
        );
      });

      // A visitor reading a quote, or tabbing through the logo link, keeps the
      // slide they are on.
      ['pointerenter', 'focusin'].forEach(function (name) {
        self.addEventListener(
          name,
          function () {
            self.held = true;
            self.stop();
          },
          { signal: signal }
        );
      });

      ['pointerleave', 'focusout'].forEach(function (name) {
        self.addEventListener(
          name,
          function () {
            self.held = false;
            self.start();
          },
          { signal: signal }
        );
      });

      this.swipe = null;

      this.addEventListener(
        'pointerdown',
        function (event) {
          if (event.pointerType === 'mouse' || !event.isPrimary) return;
          if (self.slides.length < 2) return;
          self.swipe = { id: event.pointerId, x: event.clientX, y: event.clientY };
        },
        { signal: signal }
      );

      this.addEventListener(
        'pointerup',
        function (event) {
          var swipe = self.swipe;
          self.swipe = null;
          if (!swipe || swipe.id !== event.pointerId) return;

          var dx = event.clientX - swipe.x;
          var dy = event.clientY - swipe.y;
          if (Math.abs(dx) <= SWIPE_PX || Math.abs(dx) <= Math.abs(dy)) return;

          self.goTo(self.current + (dx < 0 ? 1 : -1));
          self.restart();
        },
        { signal: signal }
      );

      this.addEventListener(
        'pointercancel',
        function () {
          self.swipe = null;
        },
        { signal: signal }
      );

      // A background tab must not race through the slides.
      global.document.addEventListener(
        'visibilitychange',
        function () {
          if (global.document.hidden) {
            self.stop();
          } else {
            self.start();
          }
        },
        { signal: signal }
      );

      this.setAttribute('data-twc-ready', '');
      this.goTo(0);
      this.start();
    }

    disconnectedCallback() {
      this.stop();
      if (this.controller) this.controller.abort();
      this.slides = [];
      this.dots = [];
      this.swipe = null;
    }

    /* ---- state ---------------------------------------------------------- */

    goTo(index) {
      var total = this.slides.length;
      if (!total) return;

      this.current = ((index % total) + total) % total;
      var currentIndex = this.current;

      this.slides.forEach(function (slide, i) {
        var on = i === currentIndex;
        if (on) {
          slide.setAttribute('data-twc-current', '');
        } else {
          slide.removeAttribute('data-twc-current');
        }
        slide.setAttribute('aria-hidden', on ? 'false' : 'true');
      });

      this.dots.forEach(function (dot, i) {
        dot.setAttribute('aria-current', i === currentIndex ? 'true' : 'false');
      });
    }

    /* ---- autoplay ------------------------------------------------------- */

    interval() {
      var seconds = parseFloat(this.getAttribute('data-autoplay') || '0');
      if (!seconds || seconds <= 0) return 0;
      return seconds * 1000;
    }

    start() {
      if (this.timer || this.held) return;
      if (this.slides.length < 2) return;
      if (reducedMotion()) return;
      if (global.document.hidden) return;

      var wait = this.interval();
      if (!wait) return;

      var self = this;
      this.timer = global.setInterval(function () {
        self.goTo(self.current + 1);
      }, wait);
    }

    stop() {
      if (!this.timer) return;
      global.clearInterval(this.timer);
      this.timer = null;
    }

    restart() {
      this.stop();
      this.start();
    }
  };

  if (global.customElements) {
    global.customElements.define('twc-content-slideshow', TwcContentSlideshow);
  }
})(window);
